import React, { useState } from 'react'
import JSZip from 'jszip'

import DeckService from 'services/deck.service'
import ToastService from 'services/toast.service'

import { useAppSelector } from 'redux/hooks'
import { DeckState, generateInitialDeckState } from 'redux/deck/initialState'

import GlobalOverlayStack from 'components/elements/Overlay/GlobalOverlayStack'
import ProgressLoadingBar from 'components/elements/ProgressLoadingBar'
import PhatDropdown from 'components/formElements/PhatDropdown'

import { sleep } from 'utils/Sleeper'
import { deckResponseToDeckState } from 'utils/responseToDeck'

import styles from './exportMultipleDecks.module.scss'

import { FileType, FileTypeOption } from 'components/deckPage/modals/Export/types'

import TextFile, { fileGenerator as textFileGenerator } from 'components/deckPage/modals/Export/TextFile'
import CSVFile, { fileGenerator as csvFileGenerator } from 'components/deckPage/modals/Export/CSVFile'
import MTGOFile, { fileGenerator as mtgoFileGenerator } from 'components/deckPage/modals/Export/MTGO'
import CheckboxFancybox from 'components/formElements/CheckboxFancybox'

const fileTypeOptions: FileTypeOption[] = [
  { label: 'Text', value: 'txt', component: TextFile },
  { label: 'CSV', value: 'csv', component: CSVFile },
  { label: 'MTGO.dek', value: 'dek', component: MTGOFile },
]

type Props = {
  open: boolean
  onClose: () => void
}

const ExportMultipleDecks = ({ open, onClose }: Props) => {
  const [fileType, setFileType] = useState<FileType>('txt')
  const [downloadingOfN, setDownloadingOfN] = useState<null | number>(null)
  const [excludeMaybeboards, setExcludeMaybeboards] = useState(false)

  const rootFolder = useAppSelector(state => state.folders.rootFolder)
  const selectedDecks = useAppSelector(state => state.folders.selectedDecks)

  // @ts-ignore
  const selectedDeckIds: number[] = Object.keys(selectedDecks).filter(deckId => selectedDecks[Number(deckId)])

  const handleDownloadAndZip = async (fileGeneratetionArguments: any) => {
    const zip = new JSZip()

    setDownloadingOfN(0)

    try {
      for (let i = 0; i < selectedDeckIds.length; i++) {
        const selectedDeckId = selectedDeckIds[i]

        setDownloadingOfN(i + 1)

        const response = await DeckService.details(selectedDeckId)
        const deck = deckResponseToDeckState(generateInitialDeckState(), response)

        const file = generateFile(deck, fileGeneratetionArguments)

        if (!file) continue

        zip.file(file.name, file.data)

        // Slows down the network requests so they're not bombarding the API
        await sleep(3000)
      }

      const result = await zip.generateAsync({ type: 'base64' })
      const element = document.createElement('a')
      const fileName = `${rootFolder.name.replace(/[^0-9a-z]/gi, '_')}-${new Date()
        .toLocaleDateString()
        .replace(/\//g, '-')}.zip`

      element.setAttribute('href', 'data:application/zip;base64,' + result)
      element.setAttribute('download', fileName)
      element.style.display = 'none'

      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
    } catch (err) {
      console.error(err)

      ToastService.create(
        'Failed to generate one or more files. If this problem continues, please contact support.',
        'Deck Export',
        'error',
      )
    } finally {
      setDownloadingOfN(null)
    }
  }

  const generateFile = (deck: DeckState, args: any) => {
    const fileName = `${deck.name.replace(/[^0-9a-z]/gi, '_')}.${fileType}`

    let cards = Object.values(deck.cardMap)
    let fileContents = ''

    if (excludeMaybeboards) cards = cards.filter(card => deck.categories[card.categories[0]]?.includedInDeck)

    try {
      if (fileType === 'txt') fileContents = textFileGenerator(cards, deck.categories, ...args)
      if (fileType === 'csv') fileContents = csvFileGenerator(cards, args)
      if (fileType === 'dek') fileContents = mtgoFileGenerator(cards, deck.categories)

      return {
        name: fileName,
        data: fileContents,
      }
    } catch (err) {
      console.error(err)

      ToastService.create(
        'Unable to generate file(s). If this keeps happening please contact support.',
        'Deck Export',
        'error',
      )
    }
  }

  const ExportForm = fileTypeOptions.find(option => option.value === fileType)?.component || TextFile

  return (
    <GlobalOverlayStack open={open} onClose={onClose} className={styles.container}>
      <div className={styles.content}>
        <h3>Export Selected Decks</h3>

        <p>
          Depending on how many decks you're attempting to export{' '}
          <span className={styles.deckCount}>
            ({selectedDeckIds.length} deck{selectedDeckIds.length > 1 ? 's' : ''})
          </span>
          , generating the files and zipping them could take a few minutes. Please be patient.
        </p>

        <CheckboxFancybox
          checked={excludeMaybeboards}
          header="Exclude maybeboard(s)"
          onClick={() => setExcludeMaybeboards(!excludeMaybeboards)}
          description="Exclude all out of deck categories (does not include Sideboard)"
          className={styles.checkbox}
        />

        <PhatDropdown
          inlineHeader
          header="Export type"
          value={fileType}
          triggerWrapperClassName={styles.fileTypeDropdown}
          options={fileTypeOptions.map(option => ({
            label: option.label,
            id: option.value,
            onClick: () => setFileType(option.value),
          }))}
        />

        <ExportForm selectedCards={[]} categoryMap={{}} fileName={''} onSubmit={args => handleDownloadAndZip(args)} />

        {downloadingOfN !== null && (
          <ProgressLoadingBar
            completionPercentage={downloadingOfN / selectedDeckIds.length}
            label={`Downloading Decks ${downloadingOfN} of ${selectedDeckIds.length}`}
          />
        )}
      </div>
    </GlobalOverlayStack>
  )

  return null
}

export default ExportMultipleDecks
